import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useAuditLogging } from "@/hooks/useAuditLogging";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  REPORT_TEMPLATES,
  generateSecuritySummaryPDF,
  generateComplianceReportPDF,
  generateFailedLoginsPDF,
  generateBackupHealthPDF,
  generateExecutivePDF,
  exportReportCSV,
  type ReportType,
} from "@/lib/reportGenerator";
import {
  FileText, Download, Clock, Shield, CheckSquare, AlertTriangle,
  Database, Calendar, Loader2, FileSpreadsheet
} from "lucide-react";

const iconMap: Record<string, React.ElementType> = {
  Shield, CheckSquare, AlertTriangle, Database, FileText,
};

interface GeneratedReport {
  id: string;
  type: ReportType;
  title: string;
  generatedAt: string;
  format: "pdf" | "csv";
}

export default function ReportsPage() {
  const { profile } = useAuth();
  const { getAuditLogs } = useAuditLogging();
  const [generating, setGenerating] = useState<string | null>(null);
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);
  const [activeTab, setActiveTab] = useState("templates");

  const fetchServers = async () => {
    const { data } = await supabase
      .from("metrics_history")
      .select("hostname, cpu_usage, memory_usage, disk_usage, firewall_enabled, failed_login_last_24h, admin_accounts_count, uptime_seconds")
      .eq("tenant_id", profile?.tenant_id || "")
      .order("timestamp", { ascending: false })
      .limit(200);
    
    // Deduplicate by hostname (latest entry)
    const map = new Map<string, any>();
    (data || []).forEach(d => { if (!map.has(d.hostname)) map.set(d.hostname, d); });
    return Array.from(map.values());
  };

  const fetchComplianceChecks = async () => {
    const { data } = await supabase
      .from("compliance_checks")
      .select("*")
      .eq("tenant_id", profile?.tenant_id || "");
    return data || [];
  };

  const fetchEvidence = async () => {
    const { data } = await supabase.functions.invoke("evidence-api", {
      body: { action: "list" },
    });
    return data?.evidence || [];
  };

  const fetchBackups = async () => {
    const { data } = await supabase
      .from("backup_restore_logs")
      .select("*")
      .eq("tenant_id", profile?.tenant_id || "");
    return data || [];
  };

  const handleGenerate = async (type: ReportType, format: "pdf" | "csv" = "pdf") => {
    setGenerating(type);
    try {
      switch (type) {
        case "security_summary": {
          const [servers, auditResult] = await Promise.all([
            fetchServers(),
            getAuditLogs(200),
          ]);
          if (format === "pdf") {
            generateSecuritySummaryPDF({ servers, alerts: [], auditLogs: auditResult?.data || [] });
          } else {
            exportReportCSV(type, auditResult?.data || []);
          }
          break;
        }
        case "compliance_report": {
          const [checks, evidence] = await Promise.all([
            fetchComplianceChecks(),
            fetchEvidence(),
          ]);
          if (format === "pdf") {
            generateComplianceReportPDF({ checks, frameworks: [], evidence });
          } else {
            exportReportCSV(type, checks);
          }
          break;
        }
        case "failed_logins": {
          const servers = await fetchServers();
          if (format === "pdf") {
            generateFailedLoginsPDF(servers);
          } else {
            exportReportCSV(type, servers.map(s => ({
              Hostname: s.hostname,
              Failed_Logins_24h: s.failed_login_last_24h || 0,
              Admin_Accounts: s.admin_accounts_count || "N/A",
              Firewall: s.firewall_enabled ? "Yes" : "No",
            })));
          }
          break;
        }
        case "backup_health": {
          const backups = await fetchBackups();
          if (format === "pdf") {
            generateBackupHealthPDF(backups);
          } else {
            exportReportCSV(type, backups);
          }
          break;
        }
        case "executive_summary": {
          const [servers, checks, evidence, auditResult] = await Promise.all([
            fetchServers(),
            fetchComplianceChecks(),
            fetchEvidence(),
            getAuditLogs(500),
          ]);
          if (format === "pdf") {
            generateExecutivePDF({ servers, complianceChecks: checks, evidence, auditLogs: auditResult?.data || [] });
          } else {
            exportReportCSV(type, servers.map(s => ({
              Hostname: s.hostname,
              CPU: s.cpu_usage,
              Memory: s.memory_usage,
              Disk: s.disk_usage,
            })));
          }
          break;
        }
      }

      const template = REPORT_TEMPLATES.find(t => t.type === type);
      setGeneratedReports(prev => [
        {
          id: crypto.randomUUID(),
          type,
          title: template?.title || type,
          generatedAt: new Date().toISOString(),
          format,
        },
        ...prev,
      ]);

      toast.success(`${format.toUpperCase()} report generated and downloaded`);
    } catch (err) {
      console.error("Report generation error:", err);
      toast.error("Failed to generate report");
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          Reports
        </h1>
        <p className="text-muted-foreground">Generate on-demand PDF and CSV reports for security, compliance, and operations</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="templates">Report Templates</TabsTrigger>
          <TabsTrigger value="history">Generated Reports ({generatedReports.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {REPORT_TEMPLATES.map((template) => {
              const Icon = iconMap[template.icon] || FileText;
              const isGenerating = generating === template.type;

              return (
                <Card key={template.type} className="flex flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{template.title}</CardTitle>
                          {template.schedule && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              <Calendar className="h-3 w-3 mr-1" />
                              {template.schedule}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <CardDescription className="mt-2">{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto">
                    <div className="flex gap-2">
                      <Button
                        className="flex-1"
                        onClick={() => handleGenerate(template.type, "pdf")}
                        disabled={!!generating}
                      >
                        {isGenerating ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        PDF
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleGenerate(template.type, "csv")}
                        disabled={!!generating}
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Generated Reports</CardTitle>
              <CardDescription>Reports generated during this session</CardDescription>
            </CardHeader>
            <CardContent>
              {generatedReports.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No reports generated yet</p>
                  <p className="text-sm">Select a template above to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {generatedReports.map((report) => (
                    <div key={report.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium">{report.title}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(report.generatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary">{report.format.toUpperCase()}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
